import { useState } from 'react';
import { Home, PieChart, User, Building2, Palette, FileText, Receipt, LogOut, Bell, TrendingUp, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const navSections = [
  {
    title: 'General',
    items: [
      { id: 'home', label: 'Home', icon: Home },
      { id: 'portfolio', label: 'Portfolio', icon: PieChart },
      { id: 'profile', label: 'Profile', icon: User }
    ]
  },
  {
    title: 'Products',
    items: [
      { id: 'land', label: 'AltSlice · Land', icon: Building2 },
      { id: 'residences', label: 'AltSlice · Residences', icon: Home },
      { id: 'orion', label: 'AltSlice · Orion Fund', icon: TrendingUp },
      { id: 'art', label: 'AltSlice · Art', icon: Palette, disabled: true }
    ]
  },
  {
    title: 'More',
    items: [
      { id: 'reports', label: 'Reports', icon: FileText },
      { id: 'transactions', label: 'Transactions', icon: Receipt }
    ]
  }
];

export default function InvestorSidebar({ activeTab, setActiveTab, user, setUser }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  return (
    <aside className="fixed top-0 left-0 h-full w-64 bg-black border-r border-white/10 z-40">
      {/* Logo/Header */}
      <div className="p-6 border-b border-white/10">
        <h1 className="text-lg font-light text-white">AltSlice</h1>
        <p className="text-xs text-gray-600 mt-1">Investor Portal</p>
      </div>

      {/* User Info */}
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#4ADE80]/20 flex items-center justify-center">
            <User className="w-5 h-5 text-[#4ADE80]" />
          </div>
          <div>
            <p className="text-sm text-white font-medium">{user?.name || 'Investor'}</p>
            <p className="text-xs text-gray-600">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 flex-1 overflow-y-auto">
        {navSections.map((section) => (
          <div key={section.title} className="mb-6">
            <p className="text-xs text-gray-600 uppercase tracking-widest mb-3 px-2">{section.title}</p>
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.disabled) return;
                      if (item.route) {
                        navigate(item.route);
                      } else {
                        setActiveTab(item.id);
                      }
                    }}
                    disabled={item.disabled}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-all ${
                      isActive ? 'bg-white/5 border-l-2 border-l-[#4ADE80] text-white' :
                      item.disabled ? 'text-gray-700 cursor-not-allowed' :
                      'text-gray-500 hover:text-white hover:bg-white/5 border-l-2 border-l-transparent'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{item.label}</span>
                    {item.disabled && <span className="text-xs text-gray-700 ml-auto">Soon</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom Section */}
      <div className="p-4 border-t border-white/10 space-y-1">
        {/* Back to Website */}
        <button 
          onClick={() => navigate('/')} 
          className="w-full flex items-center gap-3 px-4 py-3 text-gray-500 hover:text-white transition-colors"
          data-testid="exit-dashboard-btn"
        >
          <ExternalLink className="w-4 h-4" />
          <span className="text-sm">Back to Website</span>
        </button>
        
        {/* Logout */}
        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-gray-500 hover:text-white transition-colors">
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}
